// Výběr období pro analytiku.
//
// Tři režimy: posledních N dní, konkrétní měsíc, konkrétní rok. K tomu
// přepínač porovnání, který přidá stejně dlouhé období těsně předtím.
// U měsíce to vyjde na meziměsíční srovnání, u roku na meziroční,
// u čtvrtletí na mezikvartální.
import React, { useMemo } from "react";
import { CalendarRange, GitCompareArrows } from "lucide-react";

const MESICE = [
  "leden", "únor", "březen", "duben", "květen", "červen",
  "červenec", "srpen", "září", "říjen", "listopad", "prosinec",
];

const DNY = [
  { d: 7, label: "7 dní" },
  { d: 30, label: "30 dní" },
  { d: 90, label: "90 dní" },
  { d: 365, label: "rok zpět" },
];

const dvojcifer = (n) => String(n).padStart(2, "0");
const posledniDen = (rok, mesic) => new Date(rok, mesic + 1, 0).getDate();

/**
 * Spočítá rozsah dat podle nastavení filtru.
 * Vrací { od, do } ve tvaru YYYY-MM-DD, nebo null pro režim posledních dní,
 * kde stačí poslat rovnou počet dní.
 */
export function rozsahZFiltru(f) {
  if (f.rezim === "mesic") {
    const od = `${f.rok}-${dvojcifer(f.mesic + 1)}-01`;
    const doDatum = `${f.rok}-${dvojcifer(f.mesic + 1)}-${dvojcifer(posledniDen(f.rok, f.mesic))}`;
    return { od, do: doDatum };
  }
  if (f.rezim === "ctvrtleti") {
    const prvni = f.ctvrtleti * 3;
    const od = `${f.rok}-${dvojcifer(prvni + 1)}-01`;
    const posledniMesic = prvni + 2;
    const doDatum = `${f.rok}-${dvojcifer(posledniMesic + 1)}-${dvojcifer(posledniDen(f.rok, posledniMesic))}`;
    return { od, do: doDatum };
  }
  if (f.rezim === "rok") {
    return { od: `${f.rok}-01-01`, do: `${f.rok}-12-31` };
  }
  return null;
}

/** Lidský popis vybraného období, do hlavičky karty. */
export function popisObdobi(f) {
  if (f.rezim === "mesic") return `${MESICE[f.mesic]} ${f.rok}`;
  if (f.rezim === "ctvrtleti") return `${f.ctvrtleti + 1}. čtvrtletí ${f.rok}`;
  if (f.rezim === "rok") return `rok ${f.rok}`;
  const d = DNY.find((x) => x.d === f.dny);
  return d ? `posledních ${d.label}` : `posledních ${f.dny} dní`;
}

/** Jak se jmenuje srovnávané období, aby uživatel věděl, s čím to porovnáváme. */
export function popisSrovnani(f) {
  if (f.rezim === "mesic") return "předchozí měsíc";
  if (f.rezim === "ctvrtleti") return "předchozí čtvrtletí";
  if (f.rezim === "rok") return "předchozí rok";
  return "stejně dlouhé období předtím";
}

export default function FiltrObdobi({ filtr, onChange, varianta = "tmava" }) {
  const roky = useMemo(() => {
    const ted = new Date().getFullYear();
    return [ted, ted - 1, ted - 2];
  }, []);

  const set = (zmena) => onChange({ ...filtr, ...zmena });
  const svetla = varianta === "svetla";

  const ramTrida = svetla
    ? "flex rounded-xl border border-slate-200 bg-slate-50 p-0.5"
    : "flex rounded-xl border border-white/20 bg-white/10 p-0.5";

  const selectTrida = svetla
    ? "rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 outline-none transition hover:border-brand-300 focus:ring-2 focus:ring-brand-200"
    : "rounded-lg border border-white/25 bg-white/15 px-2.5 py-1.5 text-sm font-medium text-white outline-none transition hover:bg-white/25 focus:ring-2 focus:ring-white/40 [&>option]:text-slate-800";

  const tlacitkoTrida = (aktivni) =>
    `min-h-[44px] rounded-lg px-2.5 py-1.5 text-sm font-medium transition sm:min-h-0 ${
      svetla
        ? aktivni
          ? "bg-brand-600 text-white shadow-sm"
          : "text-slate-600 hover:bg-slate-200/70"
        : aktivni
          ? "bg-white text-brand-800"
          : "text-white/90 hover:bg-white/15"
    }`;

  const ikonaTrida = svetla ? "w-4 h-4 text-slate-400" : "w-4 h-4 text-white/70";

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* režim */}
      <div className={ramTrida}>
        {[
          { k: "dny", label: "Dny" },
          { k: "mesic", label: "Měsíc" },
          { k: "ctvrtleti", label: "Čtvrtletí" },
          { k: "rok", label: "Rok" },
        ].map((r) => (
          <button key={r.k} type="button" onClick={() => set({ rezim: r.k })} className={tlacitkoTrida(filtr.rezim === r.k)}>
            {r.label}
          </button>
        ))}
      </div>

      {/* upřesnění podle režimu */}
      {filtr.rezim === "dny" && (
        <div className={ramTrida}>
          {DNY.map((o) => (
            <button key={o.d} type="button" onClick={() => set({ dny: o.d })} className={tlacitkoTrida(filtr.dny === o.d)}>
              {o.label}
            </button>
          ))}
        </div>
      )}

      {filtr.rezim === "mesic" && (
        <div className="flex items-center gap-1.5">
          <CalendarRange className={ikonaTrida} />
          <select value={filtr.mesic} onChange={(e) => set({ mesic: Number(e.target.value) })} className={selectTrida}>
            {MESICE.map((m, i) => (
              <option key={m} value={i}>{m}</option>
            ))}
          </select>
          <select value={filtr.rok} onChange={(e) => set({ rok: Number(e.target.value) })} className={selectTrida}>
            {roky.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
      )}

      {filtr.rezim === "ctvrtleti" && (
        <div className="flex items-center gap-1.5">
          <CalendarRange className={ikonaTrida} />
          <select value={filtr.ctvrtleti} onChange={(e) => set({ ctvrtleti: Number(e.target.value) })} className={selectTrida}>
            {[0, 1, 2, 3].map((q) => (
              <option key={q} value={q}>{q + 1}. čtvrtletí</option>
            ))}
          </select>
          <select value={filtr.rok} onChange={(e) => set({ rok: Number(e.target.value) })} className={selectTrida}>
            {roky.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
      )}

      {filtr.rezim === "rok" && (
        <div className="flex items-center gap-1.5">
          <CalendarRange className={ikonaTrida} />
          <select value={filtr.rok} onChange={(e) => set({ rok: Number(e.target.value) })} className={selectTrida}>
            {roky.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
      )}

      {/* porovnání */}
      <button
        type="button"
        onClick={() => set({ srovnat: !filtr.srovnat })}
        title={`Porovnat s obdobím: ${popisSrovnani(filtr)}`}
        className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-medium transition sm:min-h-0 ${
          svetla
            ? filtr.srovnat
              ? "border-brand-600 bg-brand-600 text-white shadow-sm"
              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
            : filtr.srovnat
              ? "border-white bg-white text-brand-800"
              : "border-white/20 bg-white/10 text-white/90 hover:bg-white/20"
        }`}
      >
        <GitCompareArrows className="w-4 h-4" /> Porovnat
      </button>
    </div>
  );
}
